import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import PhotoUpload from './PhotoUpload';
import DynamicForm from '../../components/DynamicForm';
import { matrimonyAPI } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';

export default function ProfileCompact() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [profilePic, setProfilePic] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([null, null, null, null, null, null]);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('photos');

        // Helper to construct full image URL
        const getImageUrl = (photoUrl) => {
          if (!photoUrl) return null;
          if (photoUrl.startsWith('http')) return photoUrl;
          
          // For /media URLs, use the correct backend port (8000)
          if (photoUrl.startsWith('/media')) {
            const protocol = window.location.protocol;
            const hostname = window.location.hostname;
            const backendUrl = `${protocol}//${hostname}:8000${photoUrl}`;
            return backendUrl;
          }
          
          // For /api URLs, use the correct backend port (8000)
          if (photoUrl.startsWith('/api')) {
            const protocol = window.location.protocol;
            const hostname = window.location.hostname;
            const backendUrl = `${protocol}//${hostname}:8000${photoUrl}`;
            return backendUrl;
          }
          
          // For relative paths, assume /media prefix
          const protocol = window.location.protocol;
          const hostname = window.location.hostname;
          const fullUrl = `${protocol}//${hostname}:8000${photoUrl.startsWith('/') ? '' : '/media/'}${photoUrl}`;
          return fullUrl;
        };  useEffect(() => { 
    if (user) {
      matrimonyAPI.getProfile().then(data => {
        const initializedData = {
          ...data,
          gotra: data.gotra || '',
          time_of_birth: data.time_of_birth || '',
          place_of_birth: data.place_of_birth || '',
          permanent_address: data.permanent_address || '',
          current_address: data.current_address || '',
          number_of_brothers: data.number_of_brothers || 0,
          number_of_sisters: data.number_of_sisters || 0,
          marital_status: data.marital_status || '',
        };
        setForm(initializedData);
        
        if (data.photo) {
          const photoUrl = getImageUrl(data.photo);
          setProfilePic(photoUrl);
        }
        const galleryPhotoData = [null, null, null, null, null, null];
        for (let i = 1; i <= 6; i++) {
          const photoKey = `gallery_photo_${i}`;
          if (data[photoKey]) {
            const photoUrl = getImageUrl(data[photoKey]);
            galleryPhotoData[i - 1] = photoUrl;
          }
        }
        setGalleryPhotos(galleryPhotoData);
      }).catch(err => {
        console.error('Failed to load profile:', err);
      });
    }
  }, [user]);

  if (!user) return (
    <div className="pi"><Navbar module="consult" />
      <div className="sh">
        <span className="ey ey-r">✦ My Profile</span>
        <h1>Please <em>Login</em> First</h1>
        <div style={{ display: 'flex', gap:'clamp(0.6rem, 2vw, 1rem)', justifyContent: 'center', marginTop:'clamp(1.2rem, 3vw, 2rem)' }}>
          <button className="btn btn-rose" onClick={() => nav('/consultation/login')}>Login</button>
          <button className="btn btn-soft" onClick={() => nav('/consultation/register')}>Register</button>
        </div>
      </div>
    </div>
  );

  const upd = k => e => {
    const value = e.target.value;
    setForm(f => ({ ...f, [k]: value }));
  };

  const save = async e => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const formData = new FormData();
      
      Object.keys(form).forEach(key => {
        if (form[key] !== null && form[key] !== undefined && form[key] !== '' && key !== 'photo' && !key.startsWith('gallery_photo_')) {
          formData.append(key, form[key]);
        }
      });

      if (profilePic && profilePic.startsWith('data:')) {
        const blob = await fetch(profilePic).then(r => r.blob());
        formData.append('photo', blob, 'profile.jpg');
      }

      const galleryPromises = galleryPhotos.map((photo, index) => {
        return new Promise((resolve) => {
          if (photo && photo.startsWith('data:')) {
            fetch(photo).then(r => r.blob()).then(blob => {
              formData.append(`gallery_photo_${index + 1}`, blob, `gallery_${index + 1}.jpg`);
              resolve();
            });
          } else {
            resolve();
          }
        });
      });
      
      await Promise.all(galleryPromises);

      const token = localStorage.getItem('vt');
      const response = await fetch('/api/matrimony/profile/', {
        method: 'PUT',
        headers: { 'Authorization': `Token ${token}` },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to save');
      
      setMsg({ ok: true, text: 'Profile and photos saved successfully!' });
    }
    catch (err) { 
      setMsg({ ok: false, text: 'Could not save. Please try again.' });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="pi"><Navbar module="consult" />
      {/* COMPACT SINGLE-SCREEN LAYOUT */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: 'calc(100vh - 120px)',
        maxHeight: 'calc(100vh - 120px)',
        overflow: 'hidden',
        gap: 'clamp(0.5rem, 2vw, 1rem)',
        padding: 'clamp(0.5rem, 2vw, 1rem)'
      }}>
        
        {/* TOP: HEADER + TABS */}
        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <span className="ey ey-r">✦ Your Profile</span>
          <h2 style={{ margin: 'clamp(0.3rem, 1vw, 0.5rem) 0', fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)' }}>
            Complete Your <em>Profile</em>
          </h2>
        </div>

        {/* MESSAGE ALERT */}
        {msg && (
          <div className={`al ${msg.ok ? 'al-ok' : 'al-er'}`} style={{ 
            flex: '0 0 auto',
            padding: 'clamp(0.4rem, 1vw, 0.6rem)',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)'
          }}>
            {msg.text}
          </div>
        )}

        {/* TAB BUTTONS */}
        <div style={{
          display: 'flex',
          gap: 'clamp(0.3rem, 1vw, 0.5rem)',
          flex: '0 0 auto',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {['photos', 'form', 'subscription'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: 'clamp(0.4rem, 1vw, 0.6rem) clamp(0.8rem, 2vw, 1.2rem)',
                background: activeTab === tab ? 'var(--rose)' : 'var(--cream-l)',
                color: activeTab === tab ? '#fff' : 'var(--text)',
                border: 'none',
                borderRadius: 'var(--r)',
                cursor: 'pointer',
                fontSize: 'clamp(0.75rem, 1.5vw, 0.9rem)',
                fontWeight: activeTab === tab ? 600 : 500,
                transition: 'var(--ease)'
              }}
            >
              {tab === 'photos' && '📸 Photos'}
              {tab === 'form' && '📋 Details'}
              {tab === 'subscription' && '💳 Subscribe'}
            </button>
          ))}
        </div>

        {/* CONTENT AREA - SCROLLABLE */}
        <div style={{
          flex: '1 1 auto',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          paddingRight: 'clamp(0.3rem, 1vw, 0.6rem)'
        }}>

          {/* PHOTOS TAB */}
          {activeTab === 'photos' && (
            <div style={{ paddingBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
              <PhotoUpload 
                profilePic={profilePic} 
                setProfilePic={setProfilePic} 
                galleryPhotos={galleryPhotos} 
                setGalleryPhotos={setGalleryPhotos} 
                onSave={save} 
                saving={saving} 
              />
              <div style={{ textAlign: 'center', padding: 'clamp(1rem, 2vw, 1.5rem) 0 clamp(0.5rem, 1vw, 0.8rem) 0' }}>
                <button 
                  className="btn btn-rose" 
                  type="button"
                  disabled={saving}
                  onClick={save}
                  style={{ 
                    padding: 'clamp(0.5rem, 1.5vw, 0.7rem) clamp(1.2rem, 3vw, 2rem)',
                    fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)'
                  }}
                >
                  {saving ? 'Saving…' : 'Save Profile →'}
                </button>
              </div>
            </div>
          )}

          {/* FORM TAB */}
          {activeTab === 'form' && (
            <form onSubmit={save} style={{ paddingBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
              <DynamicForm form={form} upd={upd} loading={saving} />
              <div style={{ textAlign: 'center', padding: 'clamp(0.5rem, 1vw, 0.8rem) 0' }}>
                <button 
                  className="btn btn-rose" 
                  type="submit" 
                  disabled={saving}
                  style={{ 
                    padding: 'clamp(0.5rem, 1.5vw, 0.7rem) clamp(1.2rem, 3vw, 2rem)',
                    fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)'
                  }}
                >
                  {saving ? 'Saving…' : 'Save Profile →'}
                </button>
              </div>
            </form>
          )}

          {/* SUBSCRIPTION TAB */}
          {activeTab === 'subscription' && (
            <div style={{ paddingBottom: 'clamp(1rem, 2vw, 1.5rem)' }}>
              {form.is_active_subscriber === false ? (
                <div style={{ background: 'var(--gold-l)', border: '1px solid var(--gold)', borderRadius: 'var(--r)', padding: 'clamp(0.75rem, 2vw, 1.2rem)', textAlign: 'center' }}>
                  <div><strong style={{ color: 'var(--gold-d)', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Activate your subscription</strong></div>
                  <p style={{ fontSize: 'clamp(0.75rem, 1.5vw, 0.85rem)', color: 'var(--mid)', margin: 'clamp(0.4rem, 1vw, 0.6rem) 0' }}>Pay NPR 5,000 to unlock full matchmaking services.</p>
                  <button 
                    className="btn btn-gold" 
                    onClick={() => nav('/consultation/payment')}
                    style={{
                      padding: 'clamp(0.5rem, 1.5vw, 0.7rem) clamp(1.2rem, 3vw, 1.8rem)',
                      fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)'
                    }}
                  >
                    Pay Now →
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 'clamp(1rem, 2vw, 1.5rem)', color: 'var(--rose)' }}>
                  <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: 'clamp(0.5rem, 1vw, 0.8rem)' }}>✓</div>
                  <strong>You're a premium member!</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
